import { FileText, Users, Briefcase, Code, Layers, Cloud } from 'lucide-react';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import InterviewResultsTable from '../components/InterviewResults/InterviewResultsTable';
import StudentsDropDown from '../components/ReUsableComponents/StudentDropDown';
import { setCurrentTab } from '../redux/Slices/ResultsSlice/ResultsSlice';
import { useAppSelector } from '../redux/store/hooks';
import { getItem } from '../utils/storage';
import { cn } from '../utils/tailwindMerge';

import HRRoundResultsPage from './HRRoundResultsPage';
import ProgrammingUsersTestsResultsPage from './ProgrammingUsersTestsResultsPage';
import ResultsTablePage from './ResultsTablePage';

const ResultsPage = () => {
  const dispatch = useDispatch();
  const isCordinator = getItem('role') === '1005';
  const { userId } = useAppSelector(state => state.root.auth);
  const { selectedStudent } = useAppSelector(state => state.root.results);

  const { currentTab } = useAppSelector(state => state.root.results);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const tab = searchParams.get('tab');

    if (tab === 'cloudPrep') {
      dispatch(setCurrentTab('cloudPrep'));
    }
  }, [searchParams]);

  const tabs = [
    {
      id: 'written',
      label: 'Company Tests',
      icon: FileText,
      fullLabel: 'Company Tests'
    },
    {
      id: 'interview',
      label: 'Interview',
      icon: Users,
      fullLabel: 'Tech Interview'
    },
    {
      id: 'HR',
      label: 'HR Interview',
      icon: Briefcase,
      fullLabel: 'HR Interview'
    },
    {
      id: 'programming',
      label: 'Code Sense AI',
      icon: Code,
      fullLabel: 'Code Sense AI'
    },
    {
      id: 'Drive',
      label: 'Drive',
      icon: Layers,
      fullLabel: 'Drive'
    },
    {
      id: 'cloudPrep',
      label: 'Cloud Prep',
      icon: Cloud,
      fullLabel: 'Cloud Prep'
    }
  ];
  const currentTabIndex = tabs.findIndex(tab => tab.id === currentTab);
  const isFirst = currentTabIndex === 0;
  const isLast = currentTabIndex === tabs.length - 1;

  const tabsConatiner = () => {
    return (
      <div className='w-full overflow-hidden rounded-b-2xl  rounded-tl-xl rounded-tr-xl bg-[#eef3ff]'>
        <div className='flex hidden justify-between bg-[#eef3ff] sm:flex'>
          {tabs.map((tab, index) => {
            const Icon = tab.icon;
            const isActive = currentTab === tab.id;

            // Determine if it's the first or last tab for left/right rounding
            const isFirst = index === 0;
            const isLast = index === tabs.length - 1;

            return (
              <button
                key={tab.id}
                onClick={() =>
                  dispatch(
                    setCurrentTab(
                      tab.id as
                        | 'interview'
                        | 'written'
                        | 'HR'
                        | 'programming'
                        | 'Drive'
                        | 'cloudPrep'
                    )
                  )
                }
                className={`flex items-center gap-2 px-6 py-3 font-semibold transition-colors duration-200 ${
                  isActive
                    ? `rounded-tab ${isFirst ? 'left' : ''} ${
                        isLast ? 'right' : ''
                      } bg-[#3366FF] text-white`
                    : 'text-gray-700 hover:bg-gray-100 '
                }`}
              >
                <Icon size={18} />
                <span>{tab.fullLabel}</span>
              </button>
            );
          })}
        </div>

        <div className='flex overflow-x-auto rounded-tl-md rounded-tr-md border-b border-gray-200 bg-[#eef3ff] sm:hidden'>
          {tabs.map((tab, index) => {
            const Icon = tab.icon;
            const isActive = currentTab === tab.id;
            const isFirst = index === 0;
            const isLast = index === tabs.length - 1;

            return (
              <button
                key={tab.id}
                onClick={() =>
                  dispatch(
                    setCurrentTab(
                      tab.id as 'interview' | 'written' | 'HR' | 'programming'
                    )
                  )
                }
                className={`flex min-w-[90px] flex-col items-center gap-1 px-4 py-3 font-semibold transition-colors duration-200 md:min-w-[120px] ${
                  isActive
                    ? `rounded-tab ${isFirst ? 'left' : ''} ${
                        isLast ? 'right' : ''
                      } bg-[#3366FF] text-white`
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Icon size={18} />
                <span className='text-xs'>{tab.label}</span>
              </button>
            );
          })}
        </div>

        <div
          className={cn(
            isFirst ? 'rounded-tr-xl' : 'rounded-tl-xl',
            'border-2 border-[#3366FF] bg-[#3366FF]'
          )}
        >
          {(() => {
            switch (currentTab) {
              case 'written':
                return (
                  <ResultsTablePage
                    userId={isCordinator ? selectedStudent?.id : userId}
                  />
                );
              case 'interview':
                return (
                  <InterviewResultsTable
                    userId={isCordinator ? selectedStudent?.id : userId}
                  />
                );
              case 'HR':
                return (
                  <HRRoundResultsPage
                    userId={isCordinator ? selectedStudent?.id : userId}
                  />
                );
              case 'cloudPrep':
                return (
                  <ResultsTablePage
                    isCloudPrepTest={true}
                    userId={isCordinator ? selectedStudent?.id : userId}
                  />
                );
              case 'Drive':
                return (
                  <ResultsTablePage
                    isDriveTest={true}
                    userId={isCordinator ? selectedStudent?.id : userId}
                  />
                );
              default:
                return (
                  <ProgrammingUsersTestsResultsPage
                    userId={isCordinator ? selectedStudent?.id : userId}
                  />
                );
            }
          })()}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (isCordinator) {
      if (selectedStudent?.id === '') {
        return (
          <div className='flex h-[300px] items-center justify-center bg-opacity-70'>
            <p className='font-gully text-center text-3xl font-bold text-[#283044]'>
              Please Select a User to view their Results.
            </p>
          </div>
        );
      } else {
        return tabsConatiner();
      }
    } else {
      return tabsConatiner();
    }
  };

  return (
    <div className='mx-auto w-full p-3 sm:max-w-6xl sm:p-5 lg:p-6'>
      {isCordinator && (
        <div className='my-3'>
          <StudentsDropDown />
        </div>
      )}

      {renderContent()}
    </div>
  );
};

export default ResultsPage;
